import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    getReferralLink,
    getReferralStats,
    copyReferralLinkToClipboard,
    formatReferralStatus,
    ReferralStats,
} from '../services/referralService';
import './ReferralPage.css';

/* ---- Inline SVG illustrations ---- */

/** Friendly "friends sharing" illustration */
const HeroIllustration = () => (
    <svg viewBox="0 0 200 140" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Person left */}
        <circle cx="60" cy="58" r="18" fill="#e0e9ff" />
        <circle cx="60" cy="52" r="10" fill="#667eea" opacity="0.8" />
        <path d="M44 82c0-9 7-16 16-16s16 7 16 16" fill="#667eea" opacity="0.6" />
        {/* Person right */}
        <circle cx="140" cy="58" r="18" fill="#dcfce7" />
        <circle cx="140" cy="52" r="10" fill="#16a34a" opacity="0.8" />
        <path d="M124 82c0-9 7-16 16-16s16 7 16 16" fill="#16a34a" opacity="0.6" />
        {/* Connection arrow / heart */}
        <path d="M82 60 h36" stroke="#667eea" strokeWidth="2" strokeDasharray="4 3" />
        <circle cx="100" cy="60" r="12" fill="#fef3c7" />
        <path d="M100 55 l-5 5 5 5 5-5z" fill="#f59e0b" />
        {/* Coins */}
        <circle cx="48" cy="105" r="8" fill="#fbbf24" opacity="0.7" />
        <text x="48" y="108" textAnchor="middle" fontSize="8" fill="#92400e" fontWeight="700">$</text>
        <circle cx="152" cy="105" r="8" fill="#fbbf24" opacity="0.7" />
        <text x="152" y="108" textAnchor="middle" fontSize="8" fill="#92400e" fontWeight="700">$</text>
        {/* Sparkles */}
        <circle cx="85" cy="38" r="2" fill="#667eea" opacity="0.5" />
        <circle cx="118" cy="42" r="1.5" fill="#16a34a" opacity="0.5" />
        <circle cx="100" cy="32" r="2.5" fill="#f59e0b" opacity="0.4" />
    </svg>
);

/** Gift icon for signup reward */
const GiftIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 12 20 22 4 22 4 12" />
        <rect x="2" y="7" width="20" height="5" />
        <line x1="12" y1="22" x2="12" y2="7" />
        <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
        <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    </svg>
);

/** Star icon for subscribe reward */
const StarIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);

/** Rocket icon for empty stats */
const RocketIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
        <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
        <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
        <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
);

/* ---- Social Share SVGs ---- */
const FacebookSvg = () => (
    <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
);

const InstagramSvg = () => (
    <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
);

const XSvg = () => (
    <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
);

const ThreadsSvg = () => (
    <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.33-3.022.812-.674 1.894-1.073 3.119-1.153 1.048-.07 2.036.07 2.943.392l.015-1.503c0-.637-.025-1.143-.074-1.597-.264-2.453-1.617-3.705-4.022-3.724-1.467.012-2.66.473-3.546 1.372l-1.402-1.438C7.496 2.283 9.333 1.573 11.573 1.5h.056c1.647.009 3.065.378 4.216 1.096 1.152.718 2.007 1.73 2.543 3.01.392.936.596 2.013.607 3.201.005.566.007 1.138.005 1.713l-.002.466c.964.473 1.783 1.133 2.401 1.944.843 1.108 1.208 2.466 1.086 4.037-.152 1.97-.978 3.608-2.456 4.875-1.61 1.38-3.873 2.123-6.546 2.148zm-.129-8.847c-1.035.07-1.9.36-2.434.82-.477.41-.681.898-.646 1.538.034.606.303 1.097.757 1.382.56.352 1.325.487 2.152.444 1.03-.058 1.87-.464 2.428-1.178.405-.517.693-1.2.839-1.989-.889-.283-1.96-.464-3.096-.464z"/>
    </svg>
);

const LineSvg = () => (
    <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.105.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
    </svg>
);

const TelegramSvg = () => (
    <svg fill="currentColor" viewBox="0 0 24 24">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
);

/* ============================================ */

const ReferralPage: React.FC = () => {
    const { t, i18n } = useTranslation('referral');
    const [stats, setStats] = useState<ReferralStats | null>(null);
    const [referralLink, setReferralLink] = useState('');
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [modalCopied, setModalCopied] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showShareSheet, setShowShareSheet] = useState(false);
    const [sheetClosing, setSheetClosing] = useState(false);
    const [showInstagramNotice, setShowInstagramNotice] = useState(false);

    // Timeout refs for cleanup
    const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const modalCopyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const instagramTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Bottom sheet swipe refs
    const sheetRef = useRef<HTMLDivElement>(null);
    const dragStartY = useRef(0);
    const currentTranslateY = useRef(0);
    const isDragging = useRef(false);

    const lang = i18n.language as 'zh-TW' | 'en';

    const loadReferralData = useCallback(async () => {
        try {
            setLoading(true);
            const [linkData, statsData] = await Promise.all([
                getReferralLink(),
                getReferralStats(),
            ]);
            setReferralLink(linkData.referralLink);
            setStats(statsData);
        } catch (err) {
            console.error('Failed to load referral data:', err);
            setError(t('error.loadFailed', 'Failed to load referral data'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadReferralData();
    }, [loadReferralData]);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
            if (modalCopyTimeoutRef.current) clearTimeout(modalCopyTimeoutRef.current);
            if (instagramTimeoutRef.current) clearTimeout(instagramTimeoutRef.current);
        };
    }, []);

    // ESC to close sheet
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && showShareSheet) {
                closeSheet();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [showShareSheet]);

    // Lock body scroll when sheet is open
    useEffect(() => {
        if (showShareSheet) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showShareSheet]);

    /* ---- Sheet close with animation ---- */
    const closeSheet = useCallback(() => {
        setSheetClosing(true);
        setTimeout(() => {
            setShowShareSheet(false);
            setSheetClosing(false);
        }, 250);
    }, []);

    /* ---- Bottom sheet swipe-down handlers ---- */
    const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        dragStartY.current = touch.clientY;
        currentTranslateY.current = 0;
        isDragging.current = true;
        if (sheetRef.current) {
            sheetRef.current.style.transition = 'none';
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging.current) return;
        const touch = e.touches[0];
        const dy = touch.clientY - dragStartY.current;
        // Only allow dragging downward
        if (dy < 0) return;
        currentTranslateY.current = dy;
        if (sheetRef.current) {
            sheetRef.current.style.transform = `translateY(${dy}px)`;
        }
    };

    const handleTouchEnd = () => {
        isDragging.current = false;
        if (!sheetRef.current) return;

        sheetRef.current.style.transition = 'transform 0.25s ease-out';

        // If dragged more than 100px down, dismiss
        if (currentTranslateY.current > 100) {
            sheetRef.current.style.setProperty('--sheet-translate-y', `${currentTranslateY.current}px`);
            closeSheet();
        } else {
            // Snap back
            sheetRef.current.style.transform = 'translateY(0)';
        }
        currentTranslateY.current = 0;
    };

    /* ---- Copy & Share ---- */
    const handleCopyLink = async () => {
        const success = await copyReferralLinkToClipboard(referralLink);
        if (success) {
            setCopied(true);
            if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
            copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleModalCopy = async () => {
        const success = await copyReferralLinkToClipboard(referralLink);
        if (success) {
            setModalCopied(true);
            if (modalCopyTimeoutRef.current) clearTimeout(modalCopyTimeoutRef.current);
            modalCopyTimeoutRef.current = setTimeout(() => setModalCopied(false), 2000);
        }
    };

    // Social share links
    const shareText = t('share.text', '我正在用 Loopy 學英文，超好用！用我的連結註冊，我們都能獲得代幣獎勵！');
    const shareTextEncoded = encodeURIComponent(shareText);
    const shareLinkEncoded = encodeURIComponent(referralLink);

    const socialShareLinks = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareLinkEncoded}`,
        x: `https://twitter.com/intent/tweet?url=${shareLinkEncoded}&text=${shareTextEncoded}`,
        threads: `https://www.threads.net/intent/post?text=${shareTextEncoded}%20${shareLinkEncoded}`,
        line: `https://social-plugins.line.me/lineit/share?url=${shareLinkEncoded}`,
        telegram: `https://t.me/share/url?url=${shareLinkEncoded}&text=${shareTextEncoded}`,
    };

    const handleSocialShare = (platform: keyof typeof socialShareLinks) => {
        window.open(socialShareLinks[platform], '_blank', 'noopener,noreferrer,width=600,height=400');
        closeSheet();
    };

    const handleInstagramShare = async () => {
        await handleModalCopy();
        setShowInstagramNotice(true);
        if (instagramTimeoutRef.current) clearTimeout(instagramTimeoutRef.current);
        instagramTimeoutRef.current = setTimeout(() => setShowInstagramNotice(false), 4000);
        closeSheet();
    };

    const hasStats = stats && (stats.totalReferrals > 0 || stats.totalEarned > 0 || stats.pendingRewards > 0);

    /* ---- Render ---- */

    if (loading) {
        return (
            <div className="referral-page">
                <div className="referral-loading">
                    <div className="loading-spinner" />
                    <p>{t('loading', 'Loading...')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="referral-page">
                <div className="referral-error">
                    <p>{error}</p>
                    <button onClick={loadReferralData}>{t('retry', 'Retry')}</button>
                </div>
            </div>
        );
    }

    return (
        <div className="referral-page">
            {/* Header */}
            <div className="referral-header">
                <h1>{t('title', 'Invite Friends')}</h1>
                <p className="referral-subtitle">
                    {t('subtitle', 'Share Loopy with your friends and earn tokens together!')}
                </p>
            </div>

            {/* Illustration */}
            <div className="referral-hero-illustration">
                <HeroIllustration />
            </div>

            {/* Rewards Info */}
            <div className="rewards-info-card">
                <h2>{t('rewards.title', 'Referral Rewards')}</h2>
                <div className="rewards-grid">
                    <div className="reward-item">
                        <div className="reward-icon-wrap signup">
                            <GiftIcon />
                        </div>
                        <div className="reward-details">
                            <strong>{t('rewards.signup.title', 'Friend Signs Up')}</strong>
                            <span>{t('rewards.signup.desc', 'When your friend creates an account')}</span>
                        </div>
                        <div className="reward-tokens signup">+50</div>
                    </div>
                    <div className="reward-item">
                        <div className="reward-icon-wrap subscribe">
                            <StarIcon />
                        </div>
                        <div className="reward-details">
                            <strong>{t('rewards.subscribe.title', 'Friend Subscribes')}</strong>
                            <span>{t('rewards.subscribe.desc', 'When your friend becomes a subscriber')}</span>
                        </div>
                        <div className="reward-tokens subscribe">+200</div>
                    </div>
                </div>
                <div className="friend-bonus">
                    <span className="bonus-tag">{t('rewards.friendBonus.tag', 'Friend Bonus')}</span>
                    <p>{t('rewards.friendBonus.desc', 'Your friends also get bonus tokens when they sign up using your link!')}</p>
                </div>
            </div>

            {/* Share Section */}
            <div className="share-section">
                <h2>{t('share.sectionTitle', 'Your Referral Link')}</h2>
                <div className="referral-link-box">
                    <input
                        type="text"
                        value={referralLink}
                        readOnly
                        className="referral-link-input"
                    />
                    <button
                        onClick={handleCopyLink}
                        className={`copy-button ${copied ? 'copied' : ''}`}
                    >
                        {copied ? (
                            <>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                {t('copied', 'Copied!')}
                            </>
                        ) : (
                            <>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="9" y="9" width="13" height="13" rx="2" />
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </svg>
                                {t('copy', 'Copy')}
                            </>
                        )}
                    </button>
                </div>
                <div className="referral-code-display">
                    <span className="code-label">{t('code.label', 'Your Code:')}</span>
                    <span className="code-value">{stats?.referralCode}</span>
                </div>
                <button onClick={() => setShowShareSheet(true)} className="share-button">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="18" cy="5" r="3" />
                        <circle cx="6" cy="12" r="3" />
                        <circle cx="18" cy="19" r="3" />
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                    {t('share.button', 'Share with Friends')}
                </button>
            </div>

            {/* Stats Section */}
            <div className="stats-section">
                <h2>{t('stats.title', 'Your Referral Stats')}</h2>
                {hasStats ? (
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-value">{stats?.totalReferrals || 0}</div>
                            <div className="stat-label">{t('stats.totalReferrals', 'Total Referrals')}</div>
                        </div>
                        <div className="stat-card highlight">
                            <div className="stat-value">{stats?.totalEarned || 0}</div>
                            <div className="stat-label">{t('stats.tokensEarned', 'Tokens Earned')}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-value">{stats?.pendingRewards || 0}</div>
                            <div className="stat-label">{t('stats.pending', 'Pending Rewards')}</div>
                        </div>
                    </div>
                ) : (
                    <div className="stats-empty">
                        <div className="stats-empty-icon">
                            <RocketIcon />
                        </div>
                        <p>{t('stats.empty', 'Share your link with friends to start earning tokens!')}</p>
                    </div>
                )}
            </div>

            {/* Recent Referrals */}
            {stats?.recentReferrals && stats.recentReferrals.length > 0 && (
                <div className="recent-referrals-section">
                    <h2>{t('recent.title', 'Recent Referrals')}</h2>
                    <div className="referrals-list">
                        {stats.recentReferrals.map((referral, index) => (
                            <div key={index} className="referral-list-item">
                                <div className="referral-user">
                                    <div className="user-avatar">
                                        {referral.username.charAt(0).toUpperCase()}
                                    </div>
                                    <span className="username">{referral.username}</span>
                                </div>
                                <div className="referral-meta">
                                    <span className={`status-badge ${referral.status}`}>
                                        {formatReferralStatus(referral.status, lang)}
                                    </span>
                                    <span className="referral-date">
                                        {new Date(referral.createdAt).toLocaleDateString(lang)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Instagram Toast */}
            {showInstagramNotice && (
                <div className="instagram-toast" role="alert" aria-live="polite">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('instagram.notice', '連結已複製！請貼到 Instagram 限時動態或貼文中分享')}</span>
                </div>
            )}

            {/* Share Bottom Sheet / Modal */}
            {showShareSheet && (
                <>
                    <div
                        className={`share-overlay ${sheetClosing ? 'closing' : ''}`}
                        onClick={closeSheet}
                    />
                    <div
                        ref={sheetRef}
                        className={`share-sheet ${sheetClosing ? 'closing' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="share-sheet-title"
                    >
                        {/* Drag handle (mobile only) */}
                        <div className="share-sheet-handle">
                            <span />
                        </div>

                        <div className="share-sheet-header">
                            <h3 id="share-sheet-title">
                                {t('share.modalTitle', '分享給好友')}
                            </h3>
                            <button
                                onClick={closeSheet}
                                className="share-sheet-close"
                                aria-label={t('share.closeModal', '關閉分享視窗')}
                            >
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="share-social-grid">
                            <button
                                onClick={() => handleSocialShare('facebook')}
                                className="share-social-btn"
                                aria-label={t('share.facebook', '分享到 Facebook')}
                            >
                                <div className="share-social-icon facebook"><FacebookSvg /></div>
                                <span>Facebook</span>
                            </button>

                            <button
                                onClick={handleInstagramShare}
                                className="share-social-btn"
                                aria-label={t('share.instagram', '分享到 Instagram')}
                            >
                                <div className="share-social-icon instagram"><InstagramSvg /></div>
                                <span>Instagram</span>
                            </button>

                            <button
                                onClick={() => handleSocialShare('x')}
                                className="share-social-btn"
                                aria-label={t('share.x', '分享到 X')}
                            >
                                <div className="share-social-icon x"><XSvg /></div>
                                <span>X</span>
                            </button>

                            <button
                                onClick={() => handleSocialShare('threads')}
                                className="share-social-btn"
                                aria-label={t('share.threads', '分享到 Threads')}
                            >
                                <div className="share-social-icon threads"><ThreadsSvg /></div>
                                <span>Threads</span>
                            </button>

                            <button
                                onClick={() => handleSocialShare('line')}
                                className="share-social-btn"
                                aria-label={t('share.line', '分享到 LINE')}
                            >
                                <div className="share-social-icon line"><LineSvg /></div>
                                <span>LINE</span>
                            </button>

                            <button
                                onClick={() => handleSocialShare('telegram')}
                                className="share-social-btn"
                                aria-label={t('share.telegram', '分享到 Telegram')}
                            >
                                <div className="share-social-icon telegram"><TelegramSvg /></div>
                                <span>Telegram</span>
                            </button>
                        </div>

                        <div className="share-copy-section">
                            <p className="share-copy-hint">
                                {t('share.copyManually', '或複製連結手動分享')}
                            </p>
                            <div className="share-copy-row">
                                <input
                                    type="text"
                                    value={referralLink}
                                    readOnly
                                    aria-label={t('share.referralLink', '推薦連結')}
                                    className="share-copy-input"
                                />
                                <button
                                    onClick={handleModalCopy}
                                    className={`share-copy-btn ${modalCopied ? 'copied' : ''}`}
                                >
                                    {modalCopied ? t('copied', 'Copied!') : t('copy', 'Copy')}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ReferralPage;
